import { CircleAlert, RefreshCw, SlidersHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FilterFields, { describeChip } from "../components/googlereviews/FilterFields";
import OverviewTab from "../components/googlereviews/OverviewTab";
import ReviewsTab from "../components/googlereviews/ReviewsTab";
import TeamTab from "../components/googlereviews/TeamTab";
import TopicsTab from "../components/googlereviews/TopicsTab";
import TrendsTab from "../components/googlereviews/TrendsTab";
import {
  DEFAULT_FILTERS,
  DRILL_KEYS,
  FILTER_KEYS,
  PERIOD_PRESETS,
  TABS,
  URGENCY_LABELS,
} from "../components/googlereviews/constants";
import { EmptyState } from "../components/googlereviews/ui";
import {
  computeDateRange,
  csvCell,
  errorMessage,
  formatDate,
  formatRange,
  isAbortError,
  nf,
  parseFilters,
  resolveGranularity,
  reviewsWord,
  scrollBelowSticky,
  toApiFilters,
  toISO,
} from "../components/googlereviews/utils";
import { reviewsService } from "../services/api";
import "./css/GoogleReviews.css";

const GoogleReviews = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => parseFilters(searchParams));
  const [tab, setTab] = useState(() =>
    TABS.some((t) => t.key === searchParams.get("tab"))
      ? searchParams.get("tab")
      : "overview",
  );
  const [granularity, setGranularity] = useState("auto");
  const [options, setOptions] = useState(null);

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [list, setList] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [exporting, setExporting] = useState(false);

  const [attention, setAttention] = useState(null);
  const [attentionLoading, setAttentionLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const contentRef = useRef(null);

  const apiFilters = useMemo(() => toApiFilters(filters), [filters]);
  const filtersKey = useMemo(() => JSON.stringify(apiFilters), [apiFilters]);
  const effectiveGranularity = resolveGranularity(granularity, apiFilters);
  const compareMode = filters.period === "all" ? "none" : filters.compare;

  // Keep the URL in sync so a filtered view can be bookmarked or shared.
  useEffect(() => {
    const params = {};
    FILTER_KEYS.forEach((key) => {
      if (filters[key] !== DEFAULT_FILTERS[key]) params[key] = filters[key];
    });
    if (tab !== "overview") params.tab = tab;
    setSearchParams(params, { replace: true });
  }, [filters, tab, setSearchParams]);

  useEffect(() => {
    reviewsService
      .getFilterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    reviewsService
      .getAnalytics(apiFilters, {
        compare: compareMode,
        granularity: effectiveGranularity,
        includeNegativeDetails: false,
        signal: controller.signal,
      })
      .then((data) => {
        setAnalytics(data);
        setLoading(false);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err));
        setLoading(false);
      });
    return () => controller.abort();
  }, [apiFilters, compareMode, effectiveGranularity, reloadKey]);

  // Paginated review list – only fetched when the Opinie tab is open.
  const lastListKey = useRef(filtersKey);
  useEffect(() => {
    if (tab !== "reviews") return undefined;
    if (lastListKey.current !== filtersKey) {
      lastListKey.current = filtersKey;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }
    const controller = new AbortController();
    setListLoading(true);
    setListError(null);
    reviewsService
      .getReviews(apiFilters, {
        sort,
        page,
        pageSize,
        signal: controller.signal,
      })
      .then((data) => {
        setList(data);
        setListLoading(false);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setListError(errorMessage(err));
        setListLoading(false);
      });
    return () => controller.abort();
  }, [tab, apiFilters, filtersKey, sort, page, pageSize, reloadKey]);

  const showAttention = !filters.sentiment || filters.sentiment === "negatywna";
  useEffect(() => {
    if (tab !== "overview" || !showAttention) return undefined;
    const controller = new AbortController();
    setAttentionLoading(true);
    reviewsService
      .getReviews(
        { ...apiFilters, sentiment: "negatywna" },
        { sort: "newest", page: 1, pageSize: 4, signal: controller.signal },
      )
      .then((data) => {
        setAttention(data);
        setAttentionLoading(false);
      })
      .catch((err) => {
        if (!isAbortError(err)) {
          setAttention(null);
          setAttentionLoading(false);
        }
      });
    return () => controller.abort();
  }, [tab, apiFilters, showAttention, reloadKey]);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (patch.theme !== undefined && !patch.theme) next.themeSide = "";
      return next;
    });
  }, []);

  const changeTab = useCallback((key) => {
    setTab(key);
    scrollBelowSticky(contentRef.current);
  }, []);

  // Drill-down: narrow the filters and jump to the matching reviews.
  const drill = useCallback(
    (patch) => {
      const normalized = { ...patch };
      if ("theme" in normalized && !("themeSide" in normalized))
        normalized.themeSide = "";
      updateFilters(normalized);
      changeTab("reviews");
    },
    [updateFilters, changeTab],
  );

  const drillBucket = useCallback(
    (bucket) => {
      if (!bucket || !bucket.count) return;
      drill({
        period: "custom",
        from: bucket.start_date.slice(0, 7),
        to: bucket.end_date.slice(0, 7),
      });
    },
    [drill],
  );

  const drillYear = useCallback(
    (year) => {
      updateFilters({ period: "custom", from: `${year}-01`, to: `${year}-12` });
      changeTab("overview");
    },
    [updateFilters, changeTab],
  );

  const setSearch = useCallback(
    (value) => updateFilters({ search: value }),
    [updateFilters],
  );

  const resetFilters = () => {
    setFilters((prev) => ({ ...DEFAULT_FILTERS, compare: prev.compare }));
    setGranularity("auto");
  };

  const selectPeriod = (key) => {
    if (key === "custom") {
      const range = computeDateRange(filters);
      updateFilters({
        period: "custom",
        from:
          filters.from ||
          (range.startDate || options?.date_min || "").slice(0, 7),
        to:
          filters.to || (range.endDate || options?.date_max || "").slice(0, 7),
      });
    } else {
      updateFilters({ period: key });
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = [];
      let current = 1;
      let pages = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const data = await reviewsService.getReviews(apiFilters, {
          sort,
          page: current,
          pageSize: 100,
        });
        rows.push(...data.items);
        pages = data.pages;
        current += 1;
      } while (current <= pages);
      const header = [
        "Data",
        "Autor",
        "Ocena",
        "Sentyment",
        "Wynik sentymentu",
        "Dział",
        "Temat",
        "Pracownicy",
        "Mocne strony",
        "Słabe strony",
        "Pilność",
        "Odpowiedź firmy",
        "Treść",
      ];
      const lines = rows.map(
        ({ review, analysis, department_label: dept, topic_label: topic }) =>
          [
            review.exact_date,
            review.author,
            review.rating,
            analysis.sentiment,
            analysis.sentiment_score,
            dept,
            topic,
            analysis.employee_names.join(", "),
            analysis.strengths.join(", "),
            analysis.weaknesses.join(", "),
            URGENCY_LABELS[analysis.urgency_level] || analysis.urgency_level,
            review.owner_reply ? "tak" : "nie",
            review.text || "",
          ]
            .map(csvCell)
            .join(";"),
      );
      const blob = new Blob([`﻿${[header.join(";"), ...lines].join("\r\n")}`], {
        type: "text/csv;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `opinie-google-${toISO(new Date())}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (err) {
      setListError(`Eksport nie powiódł się: ${errorMessage(err)}`);
    } finally {
      setExporting(false);
    }
  };

  // Active filter chips (everything except the period, which has its own control).
  const chipOptions = { ...options, themeSide: filters.themeSide };
  const chips = ["department", "sentiment", "rating", ...DRILL_KEYS]
    .filter((key) => filters[key])
    .map((key) => ({
      key,
      label: describeChip(key, filters[key], chipOptions),
    }));
  const sheetCount = [
    "department",
    "sentiment",
    "rating",
    "employee",
    "product",
    "urgency",
  ].filter((k) => filters[k]).length;

  const range = computeDateRange(filters);
  const periodLabel =
    range.startDate || range.endDate
      ? formatRange(
          range.startDate || options?.date_min,
          range.endDate || options?.date_max,
        )
      : "Cały okres";

  if (!analytics && loading) {
    return (
      <div className="gr-root">
        <div
          className="gr-page-skeleton"
          aria-busy="true"
          aria-label="Ładowanie danych"
        >
          <div className="gr-skel gr-skel-header" />
          <div className="gr-skel-row">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="gr-skel gr-skel-kpi" />
            ))}
          </div>
          <div className="gr-skel gr-skel-chart" />
        </div>
      </div>
    );
  }

  if (!analytics && error) {
    return (
      <div className="gr-root">
        <div className="gr-fatal">
          <CircleAlert size={28} />
          <h2>Nie udało się załadować analizy opinii</h2>
          <p>{error}</p>
          <button
            type="button"
            className="gr-btn gr-btn-primary"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            <RefreshCw size={16} /> Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  const isEmpty = analytics && analytics.filtered_count === 0;

  return (
    <div className="gr-root">
      <header className="gr-header">
        <div>
          <h1>Opinie Google</h1>
          <p>
            Analiza opinii klientów ·{" "}
            <span className="gr-nowrap">{periodLabel}</span>
            {compareMode !== "none" && analytics?.comparison && (
              <span className="gr-nowrap">
                {" "}
                · porównanie:{" "}
                {formatRange(
                  analytics.comparison.start_date,
                  analytics.comparison.end_date,
                )}
              </span>
            )}
          </p>
        </div>
        <div className="gr-header-meta">
          <span>
            <strong>{nf(analytics?.filtered_count)}</strong> /{" "}
            {nf(analytics?.total_count)}{" "}
            {reviewsWord(analytics?.total_count || 0)}
          </span>
          {options?.date_max && (
            <span className="gr-muted">
              Najnowsza opinia w danych: {formatDate(options.date_max)}
            </span>
          )}
        </div>
      </header>

      <div className="gr-sticky">
        <div className="gr-filterbar">
          <div className="gr-periods" role="group" aria-label="Okres">
            {PERIOD_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={filters.period === p.key ? "is-active" : ""}
                aria-pressed={filters.period === p.key}
                onClick={() => selectPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="gr-btn gr-btn-ghost gr-filter-toggle"
            onClick={() => setSheetOpen(true)}
          >
            <SlidersHorizontal size={16} /> Filtry
            {sheetCount > 0 && <span className="gr-count">{sheetCount}</span>}
          </button>
        </div>

        {filters.period === "custom" && (
          <div className="gr-custom-range">
            <label className="gr-field gr-field-inline">
              <span>Od</span>
              <input
                type="month"
                value={filters.from}
                min={options?.date_min?.slice(0, 7)}
                max={filters.to || undefined}
                onChange={(e) => updateFilters({ from: e.target.value })}
              />
            </label>
            <label className="gr-field gr-field-inline">
              <span>Do</span>
              <input
                type="month"
                value={filters.to}
                min={filters.from || undefined}
                onChange={(e) => updateFilters({ to: e.target.value })}
              />
            </label>
          </div>
        )}

        {chips.length > 0 && (
          <div className="gr-chips" aria-label="Aktywne filtry">
            {chips.map((c) => (
              <button
                type="button"
                key={c.key}
                className="gr-active-chip"
                onClick={() => updateFilters({ [c.key]: "" })}
                aria-label={`Usuń filtr ${c.label}`}
              >
                {c.label} <X size={12} aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              className="gr-link-btn"
              onClick={resetFilters}
            >
              Wyczyść wszystko
            </button>
          </div>
        )}

        <nav className="gr-tabs" role="tablist" aria-label="Sekcje dashboardu">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? "is-active" : ""}
                onClick={() => changeTab(t.key)}
              >
                <Icon size={16} aria-hidden="true" /> {t.label}
              </button>
            );
          })}
        </nav>
        {loading && <div className="gr-progress" aria-hidden="true" />}
      </div>

      {error && (
        <div className="gr-alert gr-alert-error">
          <CircleAlert size={16} /> {error}
          <button
            type="button"
            className="gr-btn gr-btn-ghost"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            <RefreshCw size={14} /> Ponów
          </button>
        </div>
      )}

      <main
        ref={contentRef}
        className={`gr-content ${loading ? "is-loading" : ""}`}
      >
        {tab === "reviews" ? (
          <ReviewsTab
            list={list}
            loading={listLoading}
            error={listError}
            filters={filters}
            sort={sort}
            pageSize={pageSize}
            onSort={(v) => {
              setSort(v);
              setPage(1);
            }}
            onPageSize={(v) => {
              setPageSize(v);
              setPage(1);
            }}
            onPage={setPage}
            onFilter={updateFilters}
            onSearch={setSearch}
            onExport={exportCsv}
            exporting={exporting}
            onRetry={() => setReloadKey((k) => k + 1)}
          />
        ) : isEmpty ? (
          <EmptyState
            title="Brak opinii dla wybranych filtrów"
            text={
              options?.date_max
                ? `Dane obejmują okres ${formatRange(options.date_min, options.date_max)}.`
                : undefined
            }
            action={
              <button
                type="button"
                className="gr-btn gr-btn-primary"
                onClick={resetFilters}
              >
                Wyczyść filtry
              </button>
            }
          />
        ) : (
          <>
            {tab === "overview" && (
              <OverviewTab
                data={analytics}
                attention={attention}
                attentionLoading={attentionLoading}
                onDrill={drill}
                onBucket={drillBucket}
                showAttention={showAttention}
              />
            )}
            {tab === "topics" && <TopicsTab data={analytics} onDrill={drill} />}
            {tab === "trends" && (
              <TrendsTab
                data={analytics}
                granularity={granularity}
                onGranularity={setGranularity}
                onBucket={drillBucket}
                onYear={drillYear}
              />
            )}
            {tab === "team" && <TeamTab data={analytics} onDrill={drill} />}
          </>
        )}
      </main>

      {sheetOpen && (
        <div
          className="gr-sheet-backdrop"
          onClick={() => setSheetOpen(false)}
          role="presentation"
        >
          <div
            className="gr-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Filtry"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gr-sheet-head">
              <strong>Filtry</strong>
              <button
                type="button"
                className="gr-icon-btn"
                onClick={() => setSheetOpen(false)}
                aria-label="Zamknij"
              >
                <X size={18} />
              </button>
            </div>
            <FilterFields
              filters={filters}
              options={options}
              onChange={updateFilters}
            />
            <div className="gr-sheet-foot">
              <button
                type="button"
                className="gr-btn gr-btn-ghost"
                onClick={resetFilters}
              >
                Wyczyść
              </button>
              <button
                type="button"
                className="gr-btn gr-btn-primary"
                onClick={() => setSheetOpen(false)}
              >
                Pokaż {nf(analytics?.filtered_count)}{" "}
                {reviewsWord(analytics?.filtered_count || 0)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleReviews;
